// Tour step properties:
//   - aboveDrawer: (bool) step targets an element inside a drawer overlay — adjusts z-index accordingly
//   - awaitClick: (string) CSS selector — the tour pauses until the user clicks this element
//   - awaitClickAdvanceDelay: (number) ms to wait after the click before moving to the next step
//   - awaitClickAll: (bool) listen for click on ALL elements matching awaitClick, not just the first
//   - awaitClickDelegate: (bool) use event delegation for awaitClick — resilient to DOM re-renders (e.g. FullCalendar)
//   - awaitClickLast: (bool) target the last matching element and block clicks on all others
//   - blockClickOnlySelectors: (string[]) CSS selectors whose click events are blocked (mousedown allowed for drag-and-drop)
//   - blockClickSelectors: (string[]) CSS selectors whose click AND mousedown events are blocked
//   - blockTarget: (bool|string) true = block clicks on the spotlight target; string = block clicks on that selector
//   - blockTargetException: (string|string[]) inside a blocked target, these selectors stay clickable
//   - blockTargetSiblings: (bool) block all siblings inside the target container, only the awaitClick element stays clickable
//   - congrats: (bool) marks the final congratulatory step (triggers confetti)
//   - content: (string) i18n key for the step body text
//   - hideFinish: (bool) hide the Finish button (used when chaining to another tour)
//   - hidePrev: (bool) hide the Previous button for this step
//   - highlightDays: (bool) add a pulsing highlight to calendar day cells
//   - nextTour: (string) id of the tour to start when this step or tour ends (chaining)
//   - noSpotlight: (bool) disable the spotlight cutout (full overlay, e.g. for congrats)
//   - position: (string) tooltip placement relative to the target — top | bottom | left | right | center
//   - pulse: (bool) show a pulsing animation on the awaitClick element (default: true when awaitClick is set)
//   - raiseAncestors: (bool) raise ancestor stacking contexts so the target escapes its parent z-index
//   - scrollTo: (string) 'top' scrolls the page to top instead of centering the target
//   - spotlightPadding: (number) extra px around the target in the spotlight cutout
//   - target: (string) CSS selector of the element to highlight
//   - title: (string) i18n key for the step title
//   - waitForTarget: (bool) wait up to 30s for the target element to appear (for async data loading)
//   - watchTargets: (string[]) extra CSS selectors included in the spotlight area

import { watch } from "vue";

export default {
  id: 'reporting',
  route: 'reporting',
  steps: [
    // --- Step 1: Overview of the reporting section ---
    {
      target: '.section-reporting',
      title: 'tours.reporting.steps.overview.title',
      content: 'tours.reporting.steps.overview.content',
      position: 'center',
      spotlightPadding: 20,
      blockTarget: true,
      blockOutsideSpotlight: true,
    },
    // --- Step 2: Select a page in the sidebar ---
    {
      target: '.section-reporting .pages-sidebar',
      title: 'tours.reporting.steps.selectPage.title',
      content: 'tours.reporting.steps.selectPage.content',
      position: 'right',
      spotlightPadding: 6,
      awaitClick: '.section-reporting .pages-sidebar .page .checkbox',
      awaitClickAll: true,
      awaitClickAdvanceDelay: 400,
      pulse: false,
      blockOutsideSpotlight: true,
    },
    // --- Step 3: Close the sidebar (click show-setting) ---
    // {
    //   target: '.section-reporting .pages-sidebar .show-setting',
    //   title: 'tours.reporting.steps.closeSidebar.title',
    //   content: 'tours.reporting.steps.closeSidebar.content',
    //   position: 'bottom',
    //   spotlightPadding: 6,
    //   hidePrev: true,
    //   awaitClick: '.section-reporting .pages-sidebar .show-setting',
    //   awaitClickAdvanceDelay: 400,
    //   pulse: false
    // },
    // --- Step 4: Explore the date/period controls ---
    {
      target: '.section-reporting .reporting-date',
      title: 'tours.reporting.steps.dateControls.title',
      content: 'tours.reporting.steps.dateControls.content',
      position: 'bottom',
      spotlightPadding: 8,
      hidePrev: true,
      watchTargets: ['.date-picker', '.calendar'],
      blockOutsideSpotlight: true,
    },
    // --- Step 5: Click the search button to fetch data ---
    {
      target: '.section-reporting .search-button',
      title: 'tours.reporting.steps.searchButton.title',
      content: 'tours.reporting.steps.searchButton.content',
      position: 'left',
      spotlightPadding: 8,
      awaitClick: '.section-reporting .search-button',
      awaitClickAdvanceDelay: 6000,
      blockOutsideSpotlight: true,
    },
    // --- Step 6: Current page header ---
    {
      target: '.section-reporting .current-page',
      title: 'tours.reporting.steps.currentPage.title',
      content: 'tours.reporting.steps.currentPage.content',
      position: 'bottom',
      spotlightPadding: 8,
      hidePrev: true,
      scrollTo: 'top',
      blockTarget: true,
      blockOutsideSpotlight: true,
      waitForTarget: true,
    },
    // --- Step 7: Key metrics / informations ---
    {
      target: '.section-reporting #pageReportingLoading > .informations',
      title: 'tours.reporting.steps.informations.title',
      content: 'tours.reporting.steps.informations.content',
      position: 'left',
      blockOutsideSpotlight: true,
    },
    // --- Step 8: Overview graph ---
    {
      target: '.section-reporting .graph-wrapper',
      title: 'tours.reporting.steps.graphWrapper.title',
      content: 'tours.reporting.steps.graphWrapper.content',
      position: 'center',
      spotlightPadding: 8,
      blockOutsideSpotlight: true,
    },
    // --- Step 9: Audiences section ---
    {
      target: '.section-reporting .section-graph.audiences',
      title: 'tours.reporting.steps.audiences.title',
      content: 'tours.reporting.steps.audiences.content',
      position: 'center',
      spotlightPadding: 20,
      skipIfMissing: true,
      blockOutsideSpotlight: true,
    },
    // --- Step 10: Posts table ---
    {
      target: '.section-reporting .posts.table-wrapper',
      title: 'tours.reporting.steps.postsTable.title',
      content: 'tours.reporting.steps.postsTable.content',
      position: 'center',
      spotlightPadding: 8,
      blockOutsideSpotlight: true,
    },
    // --- Step 11: Competition section (conditional: only for facebook/instagram/linkedin/tiktok) ---
    {
      target: '.section-reporting .section-graph.concurrences',
      title: 'tours.reporting.steps.competition.title',
      content: 'tours.reporting.steps.competition.content',
      position: 'top',
      spotlightPadding: 8,
      skipIfMissing: true,
      blockOutsideSpotlight: true,
    },
    // --- Congrats ---
    {
      title: 'tours.reporting.steps.congrats.title',
      content: 'tours.reporting.steps.congrats.content',
      position: 'center',
      noSpotlight: true,
      congrats: true,
      hideFinish: true,
      nextTour: 'moderation',
      blockTarget: true,
      blockOutsideSpotlight: true,
    }
  ]
}
